import { useEffect, useState } from "react";
import { http } from "../../lib/auth";
import { useBooking } from "../../lib/booking";
import Reveal from "../Reveal";
import { ArrowUpRight } from "lucide-react";

const BIO_COLLAPSE_CHARS = 240;

/**
 * TheTeam — public stylist roster.
 * Fetches /public/booking/team (Square team members merged with Mongo
 * stylist_profiles overlay: photo_url, tagline, bio + auto-derived specialties).
 */
export default function TheTeam() {
  const [members, setMembers] = useState(null);
  const { openBooking } = useBooking();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await http.get("/public/booking/team");
        if (alive) setMembers(data.team_members || []);
      } catch {
        if (alive) setMembers([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!members || members.length === 0) return null;

  return (
    <section
      id="team"
      data-testid="team"
      style={{
        padding: "7rem 0",
        background: "var(--fs-bg)",
        borderTop: "1px solid var(--fs-line)",
      }}
    >
      <div className="fs-container">
        <div className="grid md:grid-cols-12 gap-10 mb-10 md:mb-14 items-end">
          <Reveal className="md:col-span-7">
            <p className="fs-overline" data-testid="team-overline">THE STYLISTS · CH. 04</p>
            <h2 className="fs-display-2" style={{ marginTop: "1rem" }} data-testid="team-title">
              Meet the<br />
              <span className="fs-ital">handpicked.</span>
            </h2>
          </Reveal>
          <Reveal className="md:col-span-5" delay={120}>
            <p
              style={{
                color: "var(--fs-ink-soft)",
                fontSize: "1rem",
                lineHeight: 1.7,
                maxWidth: "440px",
              }}
              data-testid="team-intro"
            >
              Every stylist on this floor was chosen by Rhon Friend — for craft,
              for taste, and for the way they treat a chair like a stage.
            </p>
          </Reveal>
        </div>

        <Reveal>
          <div
            className="grid gap-8 md:gap-10"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", alignItems: "start" }}
            data-testid="team-grid"
          >
            {members.map((m) => (
              <StylistCard key={m.id} m={m} onBook={openBooking} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StylistCard({ m, onBook }) {
  const [expanded, setExpanded] = useState(false);

  const initials = (m.full_name || "·")
    .split(" ")
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  const bio = m.bio || "";
  const isLong = bio.length > BIO_COLLAPSE_CHARS;
  const displayBio = !isLong || expanded ? bio : `${bio.slice(0, BIO_COLLAPSE_CHARS).trimEnd()}…`;

  const specialties = Array.isArray(m.specialties) ? m.specialties : [];

  return (
    <article
      data-testid={`stylist-card-${m.id}`}
      style={{
        background: "var(--fs-surface-alt)",
        border: "1px solid var(--fs-line)",
        padding: "1.4rem",
        display: "flex",
        flexDirection: "column",
        gap: "1.1rem",
      }}
    >
      <div
        style={{
          aspectRatio: "4/5",
          width: "100%",
          background: m.photo_url
            ? `center/cover no-repeat url("${m.photo_url}")`
            : "var(--fs-bg)",
          border: "1px solid var(--fs-caramel-line)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--fs-caramel)",
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "3rem",
          letterSpacing: "0.02em",
        }}
        aria-hidden={Boolean(m.photo_url)}
        data-testid={`stylist-photo-${m.id}`}
      >
        {!m.photo_url && initials}
      </div>

      <div>
        <h3
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 400,
            fontSize: "1.6rem",
            lineHeight: 1.15,
            color: "var(--fs-ink)",
          }}
          data-testid={`stylist-name-${m.id}`}
        >
          {m.full_name}
        </h3>
      </div>

      {specialties.length > 0 && (
        <div
          className="flex flex-wrap"
          style={{ gap: "0.4rem" }}
          data-testid={`stylist-specialties-${m.id}`}
        >
          {specialties.map((s) => (
            <span
              key={s}
              className="fs-overline"
              style={{
                fontSize: "0.62rem",
                padding: "0.35rem 0.7rem",
                border: "1px solid var(--fs-caramel-line)",
                color: "var(--fs-ink-soft)",
                background: "var(--fs-bg)",
                letterSpacing: "0.16em",
              }}
            >
              {s}
            </span>
          ))}
        </div>
      )}

      {bio && (
        <div>
          <p
            style={{
              color: "var(--fs-ink-soft)",
              fontSize: "0.95rem",
              lineHeight: 1.65,
              margin: 0,
              whiteSpace: "pre-line",
            }}
            data-testid={`stylist-bio-${m.id}`}
          >
            {displayBio}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="fs-overline"
              style={{
                marginTop: "0.6rem",
                background: "transparent",
                border: "none",
                padding: 0,
                color: "var(--fs-caramel)",
                cursor: "pointer",
                fontSize: "0.66rem",
                letterSpacing: "0.22em",
              }}
              data-testid={`stylist-bio-toggle-${m.id}`}
              aria-expanded={expanded}
            >
              {expanded ? "Read less" : "Read more"}
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onBook}
        className="fs-overline"
        style={{
          marginTop: "auto",
          alignSelf: "flex-start",
          background: "transparent",
          border: "none",
          padding: 0,
          color: "var(--fs-ink)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4rem",
          transition: "color 250ms ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--fs-caramel)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--fs-ink)")}
        data-testid={`stylist-book-${m.id}`}
      >
        Book with {m.first_name || m.full_name.split(" ")[0]}
        <ArrowUpRight size={14} />
      </button>
    </article>
  );
}
